import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const AddSalaryComponentDialog = ({
  open,
  onClose,
  onSubmit,
  salaryComponent,
  editMode,
  handleDelete,
}) => {
  const { t } = useTranslation();

  const typeOptions = [
    { value: "earning", label: t("admin.payroll.components.types.earning") },
    { value: "deduction", label: t("admin.payroll.components.types.deduction") },
    {
      value: "employer_contribution",
      label: t("admin.payroll.components.types.employerContribution"),
    },
  ];

  const calcTypeOptions = [
    { value: "fixed", label: t("admin.payroll.components.calcTypes.fixed") },
    { value: "percentage", label: t("admin.payroll.components.calcTypes.percentage") },
    { value: "formula", label: t("admin.payroll.components.calcTypes.formula") },
    { value: "slab", label: t("admin.payroll.components.calcTypes.slab") },
  ];

  const [form, setForm] = useState({
    code: "",
    name: "",
    type: "earning",
    calculationType: "fixed",
    taxable: true,
    statutory: false,
    active: true,
  });

  useEffect(() => {
    if (open) {
      setForm(
        salaryComponent
          ? { ...salaryComponent }
          : {
              code: "",
              name: "",
              type: "earning",
              calculationType: "fixed",
              taxable: true,
              statutory: false,
              active: true,
            }
      );
    }
  }, [open, salaryComponent]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {editMode
          ? t("admin.payroll.components.editTitle")
          : t("admin.payroll.components.addTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("admin.payroll.components.fields.code")}
            value={form.code}
            onChange={handleChange("code")}
            fullWidth
            required
          />
          <TextField
            label={t("admin.payroll.components.fields.name")}
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            required
          />
          <Autocomplete
            options={typeOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={typeOptions.find((o) => o.value === form.type) || null}
            onChange={(event, newValue) =>
              setForm((prev) => ({ ...prev, type: newValue?.value || "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.components.fields.type")} />
            )}
          />
          <Autocomplete
            options={calcTypeOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={calcTypeOptions.find((o) => o.value === form.calculationType) || null}
            onChange={(event, newValue) =>
              setForm((prev) => ({ ...prev, calculationType: newValue?.value || "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.components.fields.calculationType")} />
            )}
          />
          <FormControlLabel
            control={<Switch checked={!!form.taxable} onChange={handleChange("taxable")} />}
            label={t("admin.payroll.components.fields.taxable")}
          />
          <FormControlLabel
            control={<Switch checked={!!form.statutory} onChange={handleChange("statutory")} />}
            label={t("admin.payroll.components.fields.statutory")}
          />
          <FormControlLabel
            control={<Switch checked={!!form.active} onChange={handleChange("active")} />}
            label={t("admin.payroll.components.fields.active")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose} color="secondary">
          {t("common.cancel")}
        </MDButton>
        {editMode && (
          <MDButton
            onClick={handleDelete}
            color="error"
            sx={{ color: "red", "&:hover": { backgroundColor: "red", color: "#fff" } }}
          >
            {t("common.delete")}
          </MDButton>
        )}
        <MDButton
          onClick={handleSubmit}
          color="primary"
          disabled={!form.code?.trim() || !form.name?.trim()}
        >
          {editMode ? t("common.update") : t("common.add")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

AddSalaryComponentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  salaryComponent: PropTypes.object,
  editMode: PropTypes.bool,
  handleDelete: PropTypes.func,
};

export default AddSalaryComponentDialog;
